import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Axios from "axios";
import Select from "react-select";
import AuthContext from "../../context/AuthContext";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import "./discounts.css";

function Discount(props) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [
    selectedSubscriptionId,
    setSelectedSubscriptionId,
  ] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [
    discountPercentage,
    setDiscountPercentage,
  ] = useState("");
  const [
    discountDescription,
    setDiscountDescription,
  ] = useState("");
  const [
    editingDiscount,
    setEditingDiscount,
  ] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [addCompleted, setAddCompleted] = useState(false);

  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(
    localStorage.getItem("authTokens")
  );

  const getSubscriptions = useCallback(async () => {
    const response = await Axios.get(
      `${API_URL}/radius/subscriptions/discount/add/`,
      {
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
          "Content-Type": "application/json",
        },
        params: {
          username: user.username,
          client_id: props.client_id,
        },
      }
    );

    const records = Array.isArray(response.data)
      ? response.data
      : [];

    setSubscriptions(records);

    if (records.length === 1) {
      setSelectedSubscriptionId(
        records[0].subscription_id
      );
    }

    return records;
  }, [
    authTokens.access,
    props.client_id,
    user.username,
  ]);

  const getDiscounts = useCallback(async () => {
    const response = await Axios.get(
      `${API_URL}/radius/subscriptions/discounts/`,
      {
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
          "Content-Type": "application/json",
        },
        params: {
          username: user.username,
          client_id: props.client_id,
        },
      }
    );

    const records = Array.isArray(response.data)
      ? response.data
      : [];

    setDiscounts(records);
    return records;
  }, [
    authTokens.access,
    props.client_id,
    user.username,
  ]);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setMessage("");

      try {
        await Promise.all([
          getSubscriptions(),
          getDiscounts(),
        ]);
      } catch (error) {
        console.error(error);

        if (mounted) {
          setMessage(
            error.response?.data?.error ||
              "Unable to load this client's discounts."
          );
          setMessageType("error");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [getSubscriptions, getDiscounts]);

  const clientName =
    props.fullName || `Client ${props.client_id}`;

  const subscriptionOptions = subscriptions.map(
    (subscription) => ({
      label: subscription.username,
      value: subscription.subscription_id,
    })
  );

  const discountedSubscriptionIds = new Set(
    discounts.map((discount) =>
      String(discount.target_id)
    )
  );

  const availableSubscriptions = subscriptions.filter(
    (subscription) =>
      !discountedSubscriptionIds.has(
        String(subscription.subscription_id)
      )
  );

  const availableSubscriptionOptions =
    availableSubscriptions.map((subscription) => ({
      label: subscription.username,
      value: subscription.subscription_id,
    }));

  const activeSubscriptionOptions = editingDiscount
    ? subscriptionOptions
    : availableSubscriptionOptions;

  const selectedSubscription =
    activeSubscriptionOptions.find(
      (option) =>
        String(option.value) ===
        String(selectedSubscriptionId)
    ) || null;

  const clearForm = () => {
    setDiscountAmount("");
    setDiscountPercentage("");
    setDiscountDescription("");
    setEditingDiscount(null);
    setShowForm(false);

    if (subscriptions.length === 1) {
      setSelectedSubscriptionId(
        subscriptions[0].subscription_id
      );
    } else {
      setSelectedSubscriptionId("");
    }
  };

  const openAddForm = () => {
    setMessage("");
    setMessageType("");
    setAddCompleted(false);
    setEditingDiscount(null);
    setDiscountAmount("");
    setDiscountPercentage("");
    setDiscountDescription("");

    if (availableSubscriptions.length === 1) {
      setSelectedSubscriptionId(
        availableSubscriptions[0].subscription_id
      );
    } else {
      setSelectedSubscriptionId("");
    }

    setShowForm(true);
  };

  const openEditForm = (discount) => {
    setMessage("");
    setAddCompleted(false);
    setMessageType("");
    setEditingDiscount(discount);
    setSelectedSubscriptionId(discount.target_id);
    setDiscountAmount(String(discount.amount));
    setDiscountPercentage(
      String(discount.percentage)
    );
    setDiscountDescription(
      discount.description || ""
    );
    setShowForm(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setMessageType("");

    if (!selectedSubscriptionId) {
      setMessage(
        "Select the subscription username for this discount."
      );
      setMessageType("error");
      return;
    }

    if (
      discountAmount === "" ||
      discountPercentage === ""
    ) {
      setMessage(
        "Enter both the discount amount and percentage."
      );
      setMessageType("error");
      return;
    }

    const isEditing = Boolean(editingDiscount);

    if (
      !isEditing &&
      discountedSubscriptionIds.has(
        String(selectedSubscriptionId)
      )
    ) {
      setMessage(
        "This subscription username already has a discount."
      );
      setMessageType("error");
      return;
    }

    const requestData = {
      subscription_id: selectedSubscriptionId,
      discountAmount,
      discountPercentage,
      discountDescription,
      userdealer: user.username,
    };

    if (isEditing) {
      requestData.discount_id =
        editingDiscount.discount_id;
    }

    setIsSaving(true);

    try {
      await Axios({
        method: isEditing ? "PUT" : "POST",
        url:
          `${API_URL}/radius/subscriptions/` +
          "discount/add/",
        data: requestData,
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
          "Content-Type": "application/json",
        },
      });

      clearForm();
      setAddCompleted(true);
      setMessage(
        isEditing
          ? "Discount updated successfully."
          : "Discount added successfully."
      );
      setMessageType("success");

      window.setTimeout(() => {
        if (typeof props.close === "function") {
          props.close();
        }
      }, 900);
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.error ||
          "Unable to save the discount."
      );
      setMessageType("error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (discount) => {
    setMessage("");
    setMessageType("");
    setDeletingId(discount.discount_id);

    try {
      await Axios.delete(
        `${API_URL}/radius/subscriptions/discount/add/`,
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
          params: {
            discount_id: discount.discount_id,
            subscription_id: discount.target_id,
          },
        }
      );

      await getDiscounts();

      if (
        editingDiscount &&
        editingDiscount.discount_id ===
          discount.discount_id
      ) {
        clearForm();
      }

      setMessage("Discount deleted successfully.");
      setMessageType("success");
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.error ||
          "Unable to delete the discount."
      );
      setMessageType("error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="client-discount-panel">
      {loading ? (
        <p className="client-discount-loading">
          Loading discounts...
        </p>
      ) : (
        <>
                    {!addCompleted && (
            <section className="client-discount-summary">
              <div>
                {discounts.length > 0 ? (
                  <p>
                    <strong>{clientName}</strong>{" "}
                    {discounts.length === 1
                      ? "has one discount."
                      : `has ${discounts.length} discounts.`}
                  </p>
                ) : (
                  <p>
                    <strong>{clientName}</strong> doesn&apos;t
                    have any discounts.
                  </p>
                )}

                {subscriptions.length > 0 &&
                  availableSubscriptions.length === 0 && (
                    <p>
                      Every subscription username for this
                      client already has a discount.
                    </p>
                  )}
              </div>

              {!showForm &&
                availableSubscriptions.length > 0 && (
                  <button
                    type="button"
                    className="discount-primary-button"
                    onClick={openAddForm}
                  >
                    {discounts.length > 0
                      ? "Add Another Discount"
                      : "Add Discount"}
                  </button>
                )}
            </section>
          )}


          {message && (
            <div
              className={
                `discount-message ${messageType}`
              }
              role="status"
            >
              {message}
            </div>
          )}

          {!addCompleted && showForm && (
            <form
              className="client-discount-form"
              onSubmit={handleSubmit}
            >
              <h3>
                {editingDiscount
                  ? "Edit Discount"
                  : `Add Discount for ${clientName}`}
              </h3>

              {editingDiscount ? (
                <div className="discount-static-field">
                  <span>Subscription Username</span>
                  <strong>
                    {editingDiscount.username || "-"}
                  </strong>
                </div>
              ) : availableSubscriptions.length > 1 ? (
                <label className="discount-field">
                  <span>Subscription Username</span>
                  <Select
                    classNamePrefix="discount-select"
                    options={availableSubscriptionOptions}
                    value={selectedSubscription}
                    placeholder="Choose Username"
                    onChange={(option) =>
                      setSelectedSubscriptionId(
                        option ? option.value : ""
                      )
                    }
                  />
                </label>
              ) : (
                <div className="discount-static-field">
                  <span>Subscription Username</span>
                  <strong>
                    {availableSubscriptionOptions.length === 1
                      ? availableSubscriptionOptions[0].label
                      : "No subscription available"}
                  </strong>
                </div>
              )}

              <div className="discount-form-grid">
                <label className="discount-field">
                  <span>Discount Amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount}
                    onChange={(event) =>
                      setDiscountAmount(
                        event.target.value
                      )
                    }
                    required
                  />
                </label>

                <label className="discount-field">
                  <span>Discount Percentage</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountPercentage}
                    onChange={(event) =>
                      setDiscountPercentage(
                        event.target.value
                      )
                    }
                    required
                  />
                </label>
              </div>

              <label className="discount-field">
                <span>Description</span>
                <textarea
                  value={discountDescription}
                  onChange={(event) =>
                    setDiscountDescription(
                      event.target.value
                    )
                  }
                  placeholder="Description"
                />
              </label>

              <div className="discount-form-actions">
                <button
                  type="submit"
                  className="discount-primary-button"
                  disabled={isSaving}
                >
                  {isSaving
                    ? "Saving..."
                    : editingDiscount
                    ? "Save Changes"
                    : "Add Discount"}
                </button>

                <button
                  type="button"
                  className="discount-secondary-button"
                  onClick={clearForm}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {!addCompleted && discounts.length > 0 && (
            <section className="client-discount-list">
              {discounts.map((discount) => (
                <article
                  className="client-discount-card"
                  key={discount.discount_id}
                >
                  <header className="discount-card-header">
                    <div>
                      <span>Subscription Username</span>
                      <strong>
                        {discount.username || "-"}
                      </strong>
                    </div>

                    <span className="discount-number">
                      Discount #{discount.discount_id}
                    </span>
                  </header>

                  <div className="discount-card-values">
                    <div>
                      <span>Amount</span>
                      <strong>{discount.amount}</strong>
                    </div>

                    <div>
                      <span>Percentage</span>
                      <strong>
                        {discount.percentage}%
                      </strong>
                    </div>

                    <div>
                      <span>Created</span>
                      <strong>
                        {formatDateTime(
                          discount.created_at
                        )}
                      </strong>
                    </div>
                  </div>

                  <div className="discount-description">
                    <span>Description</span>
                    <p>{discount.description || "-"}</p>
                  </div>

                  <div className="discount-card-actions">
                    <button
                      type="button"
                      className="discount-primary-button"
                      onClick={() =>
                        openEditForm(discount)
                      }
                      disabled={deletingId !== null}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="discount-delete-button"
                      onClick={() =>
                        handleDelete(discount)
                      }
                      disabled={
                        deletingId ===
                        discount.discount_id
                      }
                    >
                      {deletingId ===
                      discount.discount_id
                        ? "Deleting..."
                        : "Delete"}
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default Discount;
